import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import {
  searchService,
  staffMandateService,
  getAgencyAgents,
  mandateService,
} from "../../services/api";
import { useDropzone } from "react-dropzone";
import { HiOutlineSearch, HiChevronDown } from "react-icons/hi";
import { useDebounce } from "../../hooks/useDebounce";
import CustomDateTimePicker from "../../components/common/CustomDateTimePicker";
import CustomDropdown from "../../components/common/CustomDropdown";
import CustomPhoneInput from "../../components/common/CustomPhoneInput";

// TP-Coach-Mandate — agent-side "Invite staff" page. Clone of InvitePlayer.js
// (the locked 1:1 template, Brief §4.1-4.3). The search step is the platform's
// standard search pattern 1:1 (Marin 2026-07-20): ONE search block cloned from
// InvitePlayer's select-player step, plus a scope selector in the nav-SearchBar
// style with the two subject scopes:
//   - "Staff"   → generic platform search (searchService.search({type:"staff"})
//     → /dashboard/search/, paginated like players). Rows carry account_id →
//     internal lane: the account is known, so the email/mobile fields are
//     hidden (D-006: in-app acceptance).
//   - "Coaches" → existing coaches search lane (searchService.search(
//     {type:"coaches"}) → /dashboard/coaches/search/, response rewrapped
//     {results:{coaches}}, NO pagination server-side — documented caveat) →
//     external lane via ts_coach id with the player photo-check moment
//     cloned 1:1 against the ts-coach check/upload endpoints.

// TheSports/SportMonks placeholder image patterns — not real coach photos.
// The authoritative photo gate is the backend check-photo endpoint; these
// only drive the amber "no photo" hint in the search results.
const PLACEHOLDER_IMAGE_PATTERNS = [
  "ndplayer_",
  "placeholder",
  "default_coach",
  "default_person",
  "/default.png",
];

/**
 * Check if an image URL is a placeholder/dummy image
 */
const isPlaceholderImage = (url) => {
  if (!url) return true;
  const urlLower = url.toLowerCase();
  return PLACEHOLDER_IMAGE_PATTERNS.some((pattern) =>
    urlLower.includes(pattern.toLowerCase())
  );
};

// Best-effort photo for a coach/staff search row (external rows expose
// photo/logo, internal rows expose photo).
const subjectPhotoUrl = (subject) => {
  if (!subject) return null;
  return (
    subject.photo ||
    subject.logo ||
    subject.image_url ||
    subject.image_path ||
    null
  );
};

/**
 * Check if a coach/staff member has a valid profile photo (not a placeholder)
 */
const subjectHasValidPhoto = (subject) => {
  const imageUrl = subjectPhotoUrl(subject);
  return Boolean(imageUrl) && !isPlaceholderImage(imageUrl);
};

// Normalize an external TheSports coach row into the shared subject shape.
// Field access mirrors the standard search coach rows (SearchTable
// renderCoachRow): logo/image_path, team object or team_name, nationality.
const normalizeExternalCoach = (coach) => ({
  source: "external",
  kind: "coach",
  ts_coach_id: coach.ts_coach_id || coach.id || coach.coach_id || null,
  account_id: null,
  name: coach.name || coach.display_name || coach.short_name || "",
  photo: subjectPhotoUrl(coach),
  role: coach.role || "Coach",
  club_name:
    (typeof coach.team === "object" ? coach.team?.name : coach.team_name) ||
    coach.club_name ||
    coach.club?.name ||
    null,
  club_logo:
    (typeof coach.team === "object"
      ? coach.team?.logo || coach.team?.image_path
      : coach.team_logo) || null,
  nationality:
    (typeof coach.nationality === "object"
      ? coach.nationality?.name
      : coach.nationality) ||
    coach.nationality_name ||
    null,
});

// Normalize a generic platform staff search row (SearchTable renderStaffRow
// contract: account_id, name, document_url, staff_type/role_type, country)
// into the shared subject shape. account_id keys the internal lane.
const normalizeStaffRow = (row) => ({
  source: "internal",
  kind: "staff",
  ts_coach_id: null,
  account_id: row.account_id || null,
  name: row.name || "",
  photo: row.document_url || subjectPhotoUrl(row),
  role:
    (typeof row.staff_type === "object"
      ? row.staff_type?.name
      : row.staff_type) ||
    (typeof row.role_type === "object"
      ? row.role_type?.name
      : row.role_type) ||
    null,
  club_name:
    (typeof row.works_for_club === "object"
      ? row.works_for_club?.name
      : row.club_name) || null,
  club_logo: null,
  nationality:
    (typeof row.country === "object" ? row.country?.name : row.country) || null,
});

// The two subject scopes — same values/labels as the nav search scopes
// (useHeaderSearch searchTypes: "staff" / "coaches").
const SEARCH_SCOPES = [
  { value: "staff", label: "Staff" },
  { value: "coaches", label: "Coaches" },
];

const InviteStaff = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const isAgencyMode = Boolean(
    location.state?.mode === "agency" ||
      (typeof window !== "undefined" &&
        window.location.pathname.includes("/agency/"))
  );
  const [formData, setFormData] = useState({
    selectedSubject: location.state?.selectedSubject || null,
    email: "",
    contractType: "mandate", // 'representation' or 'mandate'
    selectedCountries: [],
    startDate: "",
    mobile: "",
    endDate: "",
    isExclusive: false,
    agreed: false,
    agentAccountId: "",
  });

  // Active search scope (nav-SearchBar pattern): "staff" | "coaches".
  const [searchScope, setSearchScope] = useState("staff");
  const [isScopeOpen, setIsScopeOpen] = useState(false);

  // Search and modal states
  const [staffSearchQuery, setStaffSearchQuery] = useState("");
  const [staffSearchResults, setStaffSearchResults] = useState([]);
  const [searchPagination, setSearchPagination] = useState(null);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);

  // Countries data
  const [countries, setCountries] = useState([]);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [countrySearchQuery, setCountrySearchQuery] = useState("");
  const [isLoadingCountries, setIsLoadingCountries] = useState(false);
  const [showCountryDropdown, setShowCountryDropdown] = useState(false);

  // Form states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  // Agency mode: agents list
  const [agents, setAgents] = useState([]);
  const [isLoadingAgents, setIsLoadingAgents] = useState(false);

  // Coach photo states (external lane only — the photo-check moment cloned
  // 1:1 from the player flow)
  const [coachHasImage, setCoachHasImage] = useState(null); // null = not checked, true/false = checked
  const [isCheckingImage, setIsCheckingImage] = useState(false);
  const [isUploadingImage, setIsUploadingImage] = useState(false);
  const [uploadedImageUrl, setUploadedImageUrl] = useState(null);
  const [imageFile, setImageFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);

  // Refs for dropdowns
  const countryDropdownRef = useRef(null);
  const searchInputRef = useRef(null);

  // Refs for request cancellation and race condition fix
  const searchAbortControllerRef = useRef(null);
  const currentSearchQueryRef = useRef("");
  const requestIdRef = useRef(0); // Track request order to ignore stale responses

  // Debounced search
  const debouncedSearchQuery = useDebounce(staffSearchQuery, 300);

  // Load countries on component mount
  useEffect(() => {
    const loadCountries = async () => {
      setIsLoadingCountries(true);
      try {
        const response = await searchService.getCountries();
        if (response.success) {
          setCountries(response.data);
          setFilteredCountries(response.data);
        }
      } catch (error) {
        console.error("Failed to load countries:", error);
      } finally {
        setIsLoadingCountries(false);
      }
    };

    loadCountries();
  }, []);

  // Load agency agents when in agency mode (cloned from InvitePlayer)
  useEffect(() => {
    const loadAgents = async () => {
      if (!isAgencyMode) return;
      setIsLoadingAgents(true);
      try {
        // Use the new dropdown API for cleaner agent selection
        const dropdownRes = await mandateService.getAgencyAgentsDropdown();

        if (dropdownRes.success) {
          const agentOptions = (Array.isArray(dropdownRes.data) ? dropdownRes.data : []).map((agent) => ({
            account_id: agent.account_id || agent.id,
            name: agent.name,
            profile_picture_url: agent.profile_picture_url || agent.image || agent.avatar_url || null,
          })).filter(a => a.account_id && a.name);

          // If dropdown returns empty list, fallback to licensed/unlicensed endpoints
          if (agentOptions.length === 0) {
            const [licensedRes, unlicensedRes] = await Promise.all([
              getAgencyAgents({ type: "licensed" }),
              getAgencyAgents({ type: "unlicensed" }),
            ]);

            const licensed = Array.isArray(
              licensedRes?.data?.results?.data?.licensed
            )
              ? licensedRes.data.results.data.licensed
              : [];
            const unlicensed = Array.isArray(
              unlicensedRes?.data?.results?.data?.unlicensed
            )
              ? unlicensedRes.data.results.data.unlicensed
              : [];

            const combined = [...licensed, ...unlicensed];
            const uniqueByAccount = new Map();
            combined.forEach((a) => {
              if (!a) return;
              const accountId = a.account_id || a.agent_id || a.id;
              if (accountId && !uniqueByAccount.has(accountId)) {
                uniqueByAccount.set(accountId, {
                  account_id: accountId,
                  name: a.name,
                  profile_picture_url: a.profile_picture_url,
                });
              }
            });
            setAgents(Array.from(uniqueByAccount.values()));
          } else {
            setAgents(agentOptions);
          }
        } else {
          // Fallback to old API if needed
          const [licensedRes, unlicensedRes] = await Promise.all([
            getAgencyAgents({ type: "licensed" }),
            getAgencyAgents({ type: "unlicensed" }),
          ]);

          const licensed = Array.isArray(
            licensedRes?.data?.results?.data?.licensed
          )
            ? licensedRes.data.results.data.licensed
            : [];
          const unlicensed = Array.isArray(
            unlicensedRes?.data?.results?.data?.unlicensed
          )
            ? unlicensedRes.data.results.data.unlicensed
            : [];

          const combined = [...licensed, ...unlicensed];
          const uniqueByAccount = new Map();
          combined.forEach((a) => {
            if (!a) return;
            const accountId = a.account_id || a.agent_id || a.id;
            if (accountId && !uniqueByAccount.has(accountId)) {
              uniqueByAccount.set(accountId, {
                account_id: accountId,
                name: a.name,
                profile_picture_url: a.profile_picture_url,
              });
            }
          });
          setAgents(Array.from(uniqueByAccount.values()));
        }
      } catch (e) {
        console.error("Failed to load agents:", e);
        setAgents([]);
      } finally {
        setIsLoadingAgents(false);
      }
    };

    loadAgents();
  }, [isAgencyMode]);

  // Search staff/coaches when query changes or the scope switches
  useEffect(() => {
    if (debouncedSearchQuery.trim() && showSearchModal) {
      searchStaff(debouncedSearchQuery, 1);
      setCurrentPage(1);
    } else if (!debouncedSearchQuery.trim()) {
      // Cancel any pending requests
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
        searchAbortControllerRef.current = null;
      }
      setStaffSearchResults([]);
      setSearchPagination(null);
      setIsSearching(false);
      currentSearchQueryRef.current = "";
      // Reset request ID to ensure clean state
      requestIdRef.current = 0;
    }
  // searchStaff intentionally omitted from deps to avoid effect retrigger loops.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearchQuery, showSearchModal, searchScope]);

  // Cleanup: Cancel pending requests when component unmounts
  useEffect(() => {
    return () => {
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
        searchAbortControllerRef.current = null;
      }
      requestIdRef.current = 0;
    };
  }, []);

  // Cancel requests when modal closes
  useEffect(() => {
    if (!showSearchModal) {
      if (searchAbortControllerRef.current) {
        searchAbortControllerRef.current.abort();
        searchAbortControllerRef.current = null;
      }
      currentSearchQueryRef.current = "";
      requestIdRef.current = 0;
      setIsScopeOpen(false);
    }
  }, [showSearchModal]);

  // Filter countries based on search query
  useEffect(() => {
    if (countrySearchQuery.trim() === "") {
      setFilteredCountries(countries);
    } else {
      const filtered = countries.filter((country) =>
        country.name.toLowerCase().includes(countrySearchQuery.toLowerCase())
      );
      setFilteredCountries(filtered);
    }
  }, [countrySearchQuery, countries]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        countryDropdownRef.current &&
        !countryDropdownRef.current.contains(event.target)
      ) {
        setShowCountryDropdown(false);
        setCountrySearchQuery("");
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  async function searchStaff(query, page = 1) {
    // Cancel previous request if it exists
    if (searchAbortControllerRef.current) {
      searchAbortControllerRef.current.abort();
    }

    // Create new AbortController for this request
    const abortController = new AbortController();
    searchAbortControllerRef.current = abortController;

    // Increment request ID - this ensures we only process the latest request
    requestIdRef.current += 1;
    const thisRequestId = requestIdRef.current;

    // Track the query this request is for (normalized for comparison)
    const requestQuery = (query || "").trim().toLowerCase();
    currentSearchQueryRef.current = requestQuery;

    setIsSearching(true);
    try {
      let results = [];
      let pagination = null;
      if (searchScope === "coaches") {
        // Existing coaches search lane (TheSports). NOTE: this endpoint has
        // NO pagination (documented Reuse-Map caveat R5) — results are capped
        // server-side, so no pager is rendered.
        const response = await searchService.search(
          { type: "coaches", query },
          abortController.signal
        );
        if (response.success && response.data) {
          const coaches = response.data.results?.coaches || [];
          results = coaches.map(normalizeExternalCoach);
        }
      } else {
        // Generic platform staff search — same endpoint/shape as the standard
        // search page (type:"staff"), paginated like the player search.
        const response = await searchService.search(
          { type: "staff", query, per_page: 10, page },
          abortController.signal
        );
        if (response.success && response.data) {
          const staff = response.data.results?.staff || [];
          // Internal lane needs an account — rows expose account_id.
          results = staff.map(normalizeStaffRow).filter((s) => s.account_id);
          pagination = response.pagination || null;
        }
      }

      // CRITICAL CHECK: Only process if this is still the latest request
      if (thisRequestId !== requestIdRef.current) {
        return;
      }

      // Check if request was aborted
      if (abortController.signal.aborted) {
        return;
      }

      // Double-check query still matches (extra safety)
      const currentTrackedQuery = (currentSearchQueryRef.current || "").trim().toLowerCase();
      if (requestQuery !== currentTrackedQuery) {
        return;
      }

      setStaffSearchResults(results);
      setSearchPagination(pagination);
    } catch (error) {
      // Ignore abort errors (they're expected when canceling requests)
      if (
        error.name === "AbortError" ||
        error.name === "CanceledError" ||
        abortController.signal.aborted
      ) {
        return;
      }

      if (thisRequestId === requestIdRef.current) {
        console.error("Staff search error:", error);
        setStaffSearchResults([]);
        setSearchPagination(null);
      }
    } finally {
      // Only update loading state if this is still the current request
      if (thisRequestId === requestIdRef.current && !abortController.signal.aborted) {
        setIsSearching(false);
      }
    }
  }

  const handlePageChange = (page) => {
    if (staffSearchQuery.trim()) {
      setCurrentPage(page);
      // Use debounced query for consistency, but also allow page changes with current query
      const queryToUse = debouncedSearchQuery.trim() || staffSearchQuery.trim();
      if (queryToUse) {
        searchStaff(queryToUse, page);
      }
    }
  };

  const handleSubjectSelect = async (subject) => {
    setFormData((prev) => ({ ...prev, selectedSubject: subject }));
    setShowSearchModal(false);
    setStaffSearchQuery("");
    setStaffSearchResults([]);
    setSearchPagination(null);
    // Clear subject selection errors
    setErrors((prev) => ({ ...prev, selectedSubject: "" }));

    // Reset image states
    setCoachHasImage(null);
    setUploadedImageUrl(null);
    setImageFile(null);
    setImagePreview(null);

    // Photo-check moment (external TheSports lane only — internal accounts
    // accept in-app without re-biometric, D-006, so no reference photo gate)
    if (subject?.source === "external" && subject?.ts_coach_id) {
      await checkCoachPhoto(subject.ts_coach_id);
    }
  };

  const checkCoachPhoto = async (tsCoachId) => {
    setIsCheckingImage(true);
    try {
      const response = await staffMandateService.checkCoachPhoto(tsCoachId);
      if (response.success) {
        setCoachHasImage(response.has_photo);
        if (response.has_photo && response.photo_url) {
          setUploadedImageUrl(response.photo_url);
        }
      } else {
        setCoachHasImage(false);
        console.error("Failed to check coach photo:", response.error);
      }
    } catch (error) {
      console.error("Error checking coach photo:", error);
      setCoachHasImage(false);
    } finally {
      setIsCheckingImage(false);
    }
  };

  const handleImageDrop = useCallback((acceptedFiles) => {
    const file = acceptedFiles[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/jpg', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      toast.error('Invalid file type. Please upload JPEG, PNG, or WebP image.');
      return;
    }

    // Validate file size (max 5MB)
    const maxSize = 5 * 1024 * 1024; // 5MB
    if (file.size > maxSize) {
      toast.error('File too large. Maximum size is 5MB.');
      return;
    }

    setImageFile(file);

    // Create preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setImagePreview(reader.result);
    };
    reader.readAsDataURL(file);
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop: handleImageDrop,
    accept: {
      'image/jpeg': ['.jpg', '.jpeg'],
      'image/png': ['.png'],
      'image/webp': ['.webp'],
    },
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024, // 5MB
  });

  const handleUploadImage = async () => {
    if (!imageFile || !formData.selectedSubject?.ts_coach_id) {
      toast.error('Please select an image file');
      return;
    }

    setIsUploadingImage(true);
    try {
      const response = await staffMandateService.uploadCoachPhoto(
        formData.selectedSubject.ts_coach_id,
        imageFile
      );

      if (response.success) {
        setUploadedImageUrl(response.photo_url);
        setCoachHasImage(true);
        setImageFile(null);
        setImagePreview(null);
        toast.success('Coach photo uploaded successfully!');
      } else {
        toast.error(response.error || 'Failed to upload photo');
      }
    } catch (error) {
      console.error("Error uploading photo:", error);
      toast.error('Failed to upload photo. Please try again.');
    } finally {
      setIsUploadingImage(false);
    }
  };

  const handleRemoveImage = () => {
    setImageFile(null);
    setImagePreview(null);
  };

  const handleEditSubject = () => {
    setFormData((prev) => ({ ...prev, selectedSubject: null }));
    setShowSearchModal(true);
    setStaffSearchQuery("");
    setStaffSearchResults([]);
    setSearchPagination(null);
    // Reset image states when changing subject
    setCoachHasImage(null);
    setUploadedImageUrl(null);
    setImageFile(null);
    setImagePreview(null);
  };

  const handleOpenSearch = () => {
    setShowSearchModal(true);
    setTimeout(() => {
      if (searchInputRef.current) {
        searchInputRef.current.focus();
      }
    }, 100);
  };

  const handleScopeChange = (scopeValue) => {
    setIsScopeOpen(false);
    if (scopeValue === searchScope) return;
    setSearchScope(scopeValue);
    // Scope switch resets the result list; the search effect refires with the
    // kept query against the new scope.
    setStaffSearchResults([]);
    setSearchPagination(null);
    setCurrentPage(1);
  };

  const handleCountryDropdownToggle = () => {
    setShowCountryDropdown(!showCountryDropdown);
    if (!showCountryDropdown) {
      setCountrySearchQuery("");
    }
  };

  const handleCountrySelect = (country) => {
    const isSelected = formData.selectedCountries.some(
      (c) => c.id === country.id
    );

    if (isSelected) {
      // Remove country
      setFormData((prev) => ({
        ...prev,
        selectedCountries: prev.selectedCountries.filter(
          (c) => c.id !== country.id
        ),
      }));
    } else {
      // Add country
      setFormData((prev) => ({
        ...prev,
        selectedCountries: [...prev.selectedCountries, country],
      }));
    }

    // Clear country selection errors
    setErrors((prev) => ({ ...prev, selectedCountries: "" }));
  };

  const handleRemoveCountry = (countryId) => {
    setFormData((prev) => ({
      ...prev,
      selectedCountries: prev.selectedCountries.filter(
        (c) => c.id !== countryId
      ),
    }));
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));

    // Clear field errors
    setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  const isInternalSubject = formData.selectedSubject?.source === "internal";
  const isExternalSubject = formData.selectedSubject?.source === "external";

  const scopeLabel = SEARCH_SCOPES.find((s) => s.value === searchScope)?.label;

  const validateForm = () => {
    const newErrors = {};

    if (!formData.selectedSubject) {
      newErrors.selectedSubject = "Please select a coach or staff member";
    }

    // External lane: reference photo required before invite (player mechanic 1:1)
    if (isExternalSubject && coachHasImage === false && !uploadedImageUrl) {
      newErrors.coachImage = "Coach must have a profile photo. Please upload an image first.";
    }

    if (isAgencyMode && !formData.agentAccountId) {
      newErrors.agentAccountId = "Please select an agent";
    }

    // Internal-account lane: the account is known — no email/mobile collected.
    if (!isInternalSubject) {
      if (!formData.email.trim()) {
        newErrors.email = "Email is required";
      } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
        newErrors.email = "Please enter a valid email";
      }

      // Mobile number is required (react-phone-input-2 returns empty string if invalid)
      if (!formData.mobile || formData.mobile.trim() === "") {
        newErrors.mobile = "Mobile number is required";
      }
    }

    if (!formData.contractType) {
      newErrors.contractType = "Please select a contract type";
    }

    // For mandate type, countries are required
    if (
      formData.contractType === "mandate" &&
      formData.selectedCountries.length === 0
    ) {
      newErrors.selectedCountries =
        "Please select at least one country for representation";
    }

    if (!formData.startDate) {
      newErrors.startDate = "Start date is required";
    }

    if (!formData.endDate) {
      newErrors.endDate = "End date is required";
    }

    if (
      formData.startDate &&
      formData.endDate &&
      new Date(formData.startDate) >= new Date(formData.endDate)
    ) {
      newErrors.endDate = "End date must be after start date";
    }

    if (!formData.agreed) {
      newErrors.agreed = "You must agree to the terms";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setIsSubmitting(true);

    try {
      const subject = formData.selectedSubject;
      const payload = {
        contract_type: formData.contractType,
        country_ids:
          formData.contractType === "mandate"
            ? formData.selectedCountries.map((c) => c.id)
            : [],
        start_date: formData.startDate,
        end_date: formData.endDate,
        is_exclusive: formData.isExclusive,
        agreed: formData.agreed,
        ...(subject.source === "internal"
          ? { account_id: subject.account_id, kind: subject.kind }
          : { email: formData.email, mobile: formData.mobile }),
        ...(isAgencyMode && formData.agentAccountId
          ? { agent_account_id: formData.agentAccountId }
          : {}),
      };

      // Internal lane: account_id (+ kind) in the body routes the service to
      // POST /users/staff-mandate-invites/; external lane posts the TSCoach id
      // in the path (/users/coaches/<ts_coach_id>/mandate-invites/).
      const subjectId =
        subject.source === "internal" ? subject.account_id : subject.ts_coach_id;
      const response = await staffMandateService.sendMandateInvitation(
        subjectId,
        payload
      );

      if (response.success) {
        // Show success toast
        toast.success(
          `Representation invitation sent successfully to ${subject.name}!`,
          {
            duration: 5000,
            style: {
              background: "#f0fdf4",
              border: "1px solid #22c55e",
              color: "#15803d",
            },
          }
        );

        // Navigate back
        if (isAgencyMode) {
          navigate("/agency");
        } else {
          navigate("/agent/mysquad");
        }
      } else {
        // Check if error is due to missing photo (server backstop)
        if (response.requires_photo_upload) {
          setErrors({
            submit: response.error || "Coach must have a profile photo before sending invitation.",
            coachImage: "Please upload a coach photo first."
          });
          toast.error("Please upload a coach photo before sending the invitation.");
        } else {
          // Show error toast
          toast.error(response.error || "Failed to send representation invitation");
          setErrors({ submit: response.error });
        }
      }
    } catch (error) {
      console.error("Staff mandate invitation error:", error);
      const errorMessage =
        "Failed to send representation invitation. Please try again.";
      toast.error(errorMessage);
      setErrors({ submit: errorMessage });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getAgreementText = () => {
    if (formData.contractType === "representation") {
      return "By continuing, you agree to represent this coach or staff member as their agent. This representation contract will be active for the specified time period.";
    } else {
      return `By continuing, you agree to represent this coach or staff member in the selected countries for the specified time period. This representation is ${
        formData.isExclusive ? "exclusive" : "non-exclusive"
      } and will expire on the selected end date unless renewed.`;
    }
  };

  return (
    <div className="min-h-screen bg-[#F8F9FB]">
      {/* Header */}
      <div className="border-b bg-white">
        <div className="w-full px-4 md:px-8">
          <div className="flex items-center gap-2 py-4">
            <button
              onClick={() => navigate(-1)}
              className="text-gray-600 hover:text-gray-800"
            >
              <svg
                className="w-5 h-5"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M10 19l-7-7m0 0l7-7m-7 7h18"
                />
              </svg>
            </button>
            <h1 className="text-xl font-semibold text-[#202630]">
              Invite Staff
            </h1>
          </div>
        </div>
      </div>

      {/* Form */}
      <div className="w-full px-4 py-6 md:px-8 md:py-8">
        <div className="max-w-full md:max-w-[75%] mx-auto">
          <form onSubmit={handleSubmit} className="w-full space-y-8">
            {/* Subject selection — 1:1 the InvitePlayer select-player step */}
            <div className="space-y-3">
              <label className="block text-base font-medium text-black">
                Select coach or staff member
              </label>

              <div className="border border-[#E9E9E9] rounded-[5px] h-12 flex items-center">
                {formData.selectedSubject ? (
                  <div className="flex items-center justify-between w-full px-4">
                    <div className="flex items-center gap-3">
                      <div className="relative">
                        <div
                          className="w-8 h-8 rounded-full bg-gray-300 bg-cover bg-center flex items-center justify-center"
                          style={{
                            backgroundImage: subjectHasValidPhoto(formData.selectedSubject)
                              ? `url(${subjectPhotoUrl(formData.selectedSubject)})`
                              : "none",
                          }}
                        >
                          {!subjectHasValidPhoto(formData.selectedSubject) && (
                            <svg
                              className="w-4 h-4 text-gray-400"
                              fill="none"
                              stroke="currentColor"
                              viewBox="0 0 24 24"
                            >
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={1.5}
                                d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                              />
                            </svg>
                          )}
                        </div>
                        {formData.selectedSubject.source === "external" &&
                          !subjectHasValidPhoto(formData.selectedSubject) && (
                            <div className="absolute -bottom-0.5 -right-0.5 w-3 h-3 bg-amber-400 rounded-full" />
                          )}
                      </div>
                      <span className="text-base text-[#1A212F]">
                        {formData.selectedSubject.name}
                      </span>
                      {formData.selectedSubject.role && (
                        <span className="text-sm text-secondary">
                          {formData.selectedSubject.role}
                        </span>
                      )}
                    </div>
                    <button
                      type="button"
                      onClick={handleEditSubject}
                      className="text-secondary hover:text-gray-900"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"
                        />
                      </svg>
                    </button>
                  </div>
                ) : (
                  <button
                    type="button"
                    onClick={handleOpenSearch}
                    className="w-full h-full px-4 text-left"
                  >
                    <span className="text-base text-[#626262]">
                      Search for a coach or staff member...
                    </span>
                  </button>
                )}
              </div>

              {errors.selectedSubject && (
                <p className="text-sm text-red-500">{errors.selectedSubject}</p>
              )}

              {/* Coach Image Check and Upload Section (external lane only) */}
              {isExternalSubject && (
                <div className="mt-4 space-y-3">
                  {isCheckingImage ? (
                    <div className="flex items-center gap-2 text-sm text-gray-600">
                      <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                      <span>Checking coach photo...</span>
                    </div>
                  ) : coachHasImage === false ? (
                    <div className="border border-yellow-200 bg-yellow-50 rounded-lg p-4">
                      <div className="flex items-start gap-3">
                        <svg
                          className="w-5 h-5 text-yellow-600 mt-0.5 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"
                          />
                        </svg>
                        <div className="flex-1">
                          <h4 className="text-sm font-medium text-yellow-800 mb-1">
                            Coach Photo Required
                          </h4>
                          <p className="text-sm text-yellow-700 mb-3">
                            This coach does not have a profile photo. You must upload one before sending the invitation.
                          </p>
                          <ul className="text-sm text-yellow-700 mb-3 list-disc pl-5 space-y-1">
                            <li>The uploaded reference photo becomes the person's profile picture, visible immediately.</li>
                            <li>It cannot be edited or deleted by the agent without TransferPitch support.</li>
                            <li>Representation fraud leads to an irrevocable ban from TransferPitch.</li>
                          </ul>

                          {/* Image Upload Area */}
                          {!uploadedImageUrl && (
                            <div className="space-y-3">
                              {imagePreview ? (
                                <div className="relative">
                                  <img
                                    src={imagePreview}
                                    alt="Preview"
                                    className="w-full max-w-xs h-48 object-cover rounded-lg border border-gray-300"
                                  />
                                  <button
                                    type="button"
                                    onClick={handleRemoveImage}
                                    className="absolute top-2 right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600"
                                  >
                                    <svg
                                      className="w-4 h-4"
                                      fill="none"
                                      stroke="currentColor"
                                      viewBox="0 0 24 24"
                                    >
                                      <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M6 18L18 6M6 6l12 12"
                                      />
                                    </svg>
                                  </button>
                                </div>
                              ) : (
                                <div
                                  {...getRootProps()}
                                  className={`border-2 border-dashed rounded-lg p-6 text-center cursor-pointer transition-colors ${
                                    isDragActive
                                      ? "border-blue-500 bg-blue-50"
                                      : "border-gray-300 hover:border-gray-400"
                                  }`}
                                >
                                  <input {...getInputProps()} />
                                  <svg
                                    className="w-12 h-12 text-gray-400 mx-auto mb-3"
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                  >
                                    <path
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                      strokeWidth={2}
                                      d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                  </svg>
                                  <p className="text-sm text-gray-600 mb-1">
                                    {isDragActive
                                      ? "Drop the image here"
                                      : "Drag & drop an image here, or click to select"}
                                  </p>
                                  <p className="text-xs text-gray-500">
                                    JPEG, PNG, or WebP (max 5MB)
                                  </p>
                                </div>
                              )}

                              {imageFile && (
                                <button
                                  type="button"
                                  onClick={handleUploadImage}
                                  disabled={isUploadingImage}
                                  className="w-full py-2 px-4 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                                >
                                  {isUploadingImage ? (
                                    <>
                                      <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                                      <span>Uploading...</span>
                                    </>
                                  ) : (
                                    <>
                                      <svg
                                        className="w-4 h-4"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                                        />
                                      </svg>
                                      <span>Upload Photo</span>
                                    </>
                                  )}
                                </button>
                              )}
                            </div>
                          )}

                          {uploadedImageUrl && (
                            <div className="mt-3 p-3 bg-green-50 border border-green-200 rounded-lg">
                              <div className="flex items-center gap-2 text-sm text-green-800">
                                <svg
                                  className="w-5 h-5"
                                  fill="currentColor"
                                  viewBox="0 0 20 20"
                                >
                                  <path
                                    fillRule="evenodd"
                                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                                    clipRule="evenodd"
                                  />
                                </svg>
                                <span>Photo uploaded successfully!</span>
                              </div>
                              <img
                                src={uploadedImageUrl}
                                alt="Uploaded"
                                className="mt-2 w-24 h-24 object-cover rounded-lg border border-green-300"
                              />
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  ) : coachHasImage === true && uploadedImageUrl ? (
                    <div className="border border-green-200 bg-green-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 text-sm text-green-800 mb-2">
                        <svg
                          className="w-5 h-5"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z"
                            clipRule="evenodd"
                          />
                        </svg>
                        <span>Coach has a profile photo</span>
                      </div>
                      <p className="text-sm text-green-700 mb-2">This is the reference photo used for identity verification.</p>
                      <img
                        src={uploadedImageUrl}
                        alt="Coach"
                        className="w-24 h-24 object-cover rounded-lg border border-green-300"
                      />
                    </div>
                  ) : null}

                  {errors.coachImage && (
                    <p className="text-sm text-red-500">{errors.coachImage}</p>
                  )}
                </div>
              )}
            </div>

            {/* Staff Search Modal */}
            {showSearchModal && (
              <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
                <div className="bg-white rounded-lg w-full max-w-4xl mx-4 max-h-[80vh] flex flex-col">
                  {/* Modal Header */}
                  <div className="flex items-center justify-between p-6 border-b">
                    <h2 className="text-xl font-semibold text-[#202630]">
                      {searchScope === "coaches"
                        ? "Search Coaches"
                        : "Search Staff"}
                    </h2>
                    <button
                      type="button"
                      onClick={() => setShowSearchModal(false)}
                      className="text-gray-500 hover:text-gray-700"
                    >
                      <svg
                        className="w-6 h-6"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      </svg>
                    </button>
                  </div>

                  {/* Search Input — nav-SearchBar pill: scope segment on the
                      left, hairline divider, then search icon + input */}
                  <div className="p-6 border-b">
                    <div className="relative w-full">
                      <div className="flex items-center h-12 w-full rounded-xl bg-[#F3F6FB] border border-transparent hover:border-gray-200 box-border">
                        {/* Scope segment (left) */}
                        <button
                          type="button"
                          title={scopeLabel}
                          aria-label={`Search scope: ${scopeLabel}`}
                          onClick={() => setIsScopeOpen(!isScopeOpen)}
                          className="flex items-center gap-1.5 h-12 pl-3.5 pr-2.5 rounded-l-xl text-[13px] font-semibold text-[#0F1419] hover:bg-[#E9EEF6] flex-shrink-0 whitespace-nowrap"
                        >
                          <span>{scopeLabel}</span>
                          <HiChevronDown
                            className={`w-3.5 h-3.5 text-gray-500 transition-transform ${
                              isScopeOpen ? "rotate-180" : ""
                            }`}
                          />
                        </button>

                        {/* Divider */}
                        <div className="w-px h-5 bg-[#E0E4EA] flex-shrink-0" />

                        {/* Search area */}
                        <div className="flex items-center gap-2.5 flex-1 min-w-0 px-3.5">
                          <HiOutlineSearch className="w-[18px] h-[18px] text-gray-400 flex-shrink-0" />
                          <input
                            ref={searchInputRef}
                            type="text"
                            placeholder={
                              searchScope === "coaches"
                                ? "Search for a coach..."
                                : "Search for a staff member..."
                            }
                            value={staffSearchQuery}
                            onChange={(e) =>
                              setStaffSearchQuery(e.target.value)
                            }
                            className="w-full min-w-0 bg-transparent text-base text-[#0F1419] placeholder:text-[#626262] border-0 focus:outline-none focus:ring-0 p-0"
                          />
                        </div>
                        {isSearching && (
                          <div className="pr-3.5 flex-shrink-0">
                            <div className="animate-spin w-4 h-4 border-2 border-blue-500 border-t-transparent rounded-full" />
                          </div>
                        )}
                      </div>

                      {/* Scope dropdown */}
                      {isScopeOpen && (
                        <div className="absolute left-0 top-[52px] w-[200px] bg-white border border-gray-200 rounded-xl shadow-[0_12px_32px_-12px_rgba(15,20,25,0.25)] p-1.5 z-50">
                          {SEARCH_SCOPES.map((scope) => {
                            const active = scope.value === searchScope;
                            return (
                              <button
                                key={scope.value}
                                type="button"
                                onClick={() => handleScopeChange(scope.value)}
                                className={`w-full text-left px-3 py-2 rounded-lg text-[13.5px] font-medium hover:bg-[#F3F6FB] flex items-center justify-between ${
                                  active
                                    ? "text-[#2283FF] bg-[#EEF4FE]"
                                    : "text-[#0F1419]"
                                }`}
                              >
                                <span>{scope.label}</span>
                                {active && (
                                  <svg
                                    width="14"
                                    height="14"
                                    viewBox="0 0 24 24"
                                    fill="none"
                                  >
                                    <path
                                      d="M5 12.5l4.5 4.5L19 7.5"
                                      stroke="#2283FF"
                                      strokeWidth="2"
                                      strokeLinecap="round"
                                      strokeLinejoin="round"
                                    />
                                  </svg>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Search Results */}
                  <div className="flex-1 overflow-y-auto p-6">
                    {isSearching && staffSearchResults.length === 0 ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="text-center">
                          <div className="animate-spin w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full mx-auto mb-2" />
                          <p className="text-secondary">
                            {searchScope === "coaches"
                              ? "Searching coaches..."
                              : "Searching staff..."}
                          </p>
                        </div>
                      </div>
                    ) : staffSearchResults.length > 0 ? (
                      <div className="space-y-4">
                        {/* Results List */}
                        <div className="space-y-2">
                        {staffSearchResults.map((subject) => (
                          <button
                            key={
                              subject.ts_coach_id ||
                              subject.account_id ||
                              subject.name
                            }
                            type="button"
                            onClick={() => handleSubjectSelect(subject)}
                            className="w-full p-4 border border-[#E9E9E9] rounded-lg hover:bg-gray-50 hover:border-blue-300 transition-colors text-left"
                          >
                            <div className="flex items-center gap-4">
                              <div className="relative flex-shrink-0">
                                <div
                                  className="w-12 h-12 rounded-full bg-gray-300 bg-cover bg-center"
                                  style={{
                                    backgroundImage: subjectHasValidPhoto(subject)
                                      ? `url(${subjectPhotoUrl(subject)})`
                                      : "none",
                                  }}
                                >
                                  {/* Show person icon if no valid photo */}
                                  {!subjectHasValidPhoto(subject) && (
                                    <div className="w-full h-full flex items-center justify-center">
                                      <svg
                                        className="w-6 h-6 text-gray-400"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={1.5}
                                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                                        />
                                      </svg>
                                    </div>
                                  )}
                                </div>
                                {/* No photo badge (external lane — photo gate applies) */}
                                {subject.source === "external" &&
                                  !subjectHasValidPhoto(subject) && (
                                    <div className="absolute -bottom-1 -right-1 w-5 h-5 bg-amber-400 rounded-full flex items-center justify-center">
                                      <svg
                                        className="w-3 h-3 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                      >
                                        <path
                                          strokeLinecap="round"
                                          strokeLinejoin="round"
                                          strokeWidth={2}
                                          d="M12 9v2m0 4h.01"
                                        />
                                      </svg>
                                    </div>
                                  )}
                              </div>

                              <div className="flex-1 min-w-0">
                                <div className="flex items-center justify-between mb-1">
                                  <h3 className="text-base font-medium text-[#1A212F] truncate">
                                    {subject.name}
                                  </h3>
                                </div>
                                <div className="flex items-center gap-4 text-sm text-gray-600 flex-wrap">
                                  {/* Role (staff rows) */}
                                  {subject.source === "internal" &&
                                    subject.role && (
                                      <span className="flex-shrink-0">
                                        {subject.role}
                                      </span>
                                    )}

                                  {/* Club/Team with logo beside */}
                                  {subject.club_name && (
                                    <span className="flex items-center gap-2 flex-shrink-0">
                                      {subject.club_logo && (
                                        <img
                                          src={subject.club_logo}
                                          alt={subject.club_name}
                                          className="w-5 h-5 object-contain rounded-full"
                                          onError={(e) => {
                                            e.target.style.display = "none";
                                          }}
                                        />
                                      )}
                                      <span className="truncate max-w-[180px]">
                                        {subject.club_name}
                                      </span>
                                    </span>
                                  )}

                                  {/* Nationality/Country */}
                                  {subject.nationality && (
                                    <span className="truncate max-w-[180px]">
                                      {subject.nationality}
                                    </span>
                                  )}
                                </div>
                              </div>
                            </div>
                          </button>
                        ))}
                        </div>

                        {/* Pagination (staff scope — coaches endpoint has none) */}
                        {searchPagination && (
                          <div className="pt-4 border-t">
                            <div className="flex items-center justify-between">
                              <div className="text-sm text-secondary">
                                Showing {staffSearchResults.length} of{" "}
                                {searchPagination.count || 0} results
                                {searchPagination.total_pages > 1 && (
                                  <span className="ml-2">
                                    (Page {currentPage} of{" "}
                                    {searchPagination.total_pages})
                                  </span>
                                )}
                              </div>

                              {(searchPagination.total_pages > 1 ||
                                searchPagination.has_more) && (
                                <div className="flex items-center gap-2">
                                  <button
                                    type="button"
                                    onClick={() =>
                                      handlePageChange(currentPage - 1)
                                    }
                                    disabled={currentPage <= 1}
                                    className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                  >
                                    Previous
                                  </button>

                                  <span className="px-2 text-sm text-secondary">
                                    {currentPage}
                                  </span>

                                  <button
                                    type="button"
                                    onClick={() =>
                                      handlePageChange(currentPage + 1)
                                    }
                                    disabled={
                                      !searchPagination.has_more &&
                                      currentPage >=
                                        searchPagination.total_pages
                                    }
                                    className="px-3 py-1 border rounded text-sm disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
                                  >
                                    Next
                                  </button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ) : staffSearchQuery.trim() && !isSearching ? (
                      <div className="flex items-center justify-center h-32">
                        <div className="text-center">
                          <svg
                            className="w-12 h-12 text-gray-300 mx-auto mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                          <p className="text-secondary">
                            {searchScope === "coaches"
                              ? `No coaches found for "${staffSearchQuery}"`
                              : `No staff found for "${staffSearchQuery}"`}
                          </p>
                          <p className="text-sm text-gray-400 mt-1">
                            Try adjusting your search terms
                          </p>
                        </div>
                      </div>
                    ) : (
                      <div className="flex items-center justify-center h-32">
                        <div className="text-center">
                          <svg
                            className="w-12 h-12 text-gray-300 mx-auto mb-2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                          <p className="text-secondary">
                            {searchScope === "coaches"
                              ? "Start typing to search for coaches"
                              : "Start typing to search for staff"}
                          </p>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Email + Mobile — hidden for the internal-account lane (the
                account is known; acceptance happens in-app, D-006).
                D-028: the placeholders follow the active scope. This form
                invites BOTH coaches and non-coach staff (a physio is not a
                coach), the same distinction the page's own copy already makes
                ("represent this coach or staff member …"), so the coach
                wording only stands in the coaches lane. */}
            {!isInternalSubject && (
              <>
                {/* Email Field */}
                <div className="space-y-3">
                  <label className="block text-base font-medium text-black">
                    Email address
                    <span className="text-red-500 ml-1">*</span>
                  </label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder={
                      searchScope === "coaches"
                        ? "Enter the coach's email address"
                        : "Enter the staff member's email address"
                    }
                    className="w-full h-12 px-4 border border-[#E9E9E9] rounded-[5px] text-base placeholder-[#626262] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                  {errors.email && (
                    <p className="text-sm text-red-500">{errors.email}</p>
                  )}
                </div>

                {/* Mobile Number Field */}
                <div className="space-y-3">
                  <CustomPhoneInput
                    label="Mobile number"
                    value={formData.mobile}
                    onChange={(value) => {
                      setFormData((prev) => ({ ...prev, mobile: value }));
                      // Clear mobile errors
                      setErrors((prev) => ({ ...prev, mobile: "" }));
                    }}
                    placeholder={
                      searchScope === "coaches"
                        ? "Enter the coach's mobile number"
                        : "Enter the staff member's mobile number"
                    }
                    required
                    error={errors.mobile}
                    className="!h-12"
                  />
                </div>
              </>
            )}

            {/* Agent Selection - Only for agency mode */}
            {isAgencyMode && (
              <div className="space-y-3">
                <label className="block text-base font-medium text-black">
                  Select agent
                </label>
                <CustomDropdown
                  options={agents.map((a) => ({
                    label: a.name,
                    value: a.account_id,
                    image: a.profile_picture_url || undefined,
                  }))}
                  value={formData.agentAccountId}
                  onChange={(val) =>
                    setFormData((prev) => ({ ...prev, agentAccountId: val }))
                  }
                  placeholder={
                    isLoadingAgents ? "Loading agents..." : "Select an agent"
                  }
                  searchEnabled
                  disabled={isLoadingAgents}
                />
                {errors.agentAccountId && (
                  <p className="text-sm text-red-500">
                    {errors.agentAccountId}
                  </p>
                )}
              </div>
            )}

            {/* Contract Type Selection */}
            <div className="space-y-3">
              <label className="block text-base font-medium text-black">
                Select agreement type for this staff member
              </label>

              <div className="flex flex-col gap-4 md:flex-row md:gap-10">
                {/* Representation Contract */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className="relative">
                    <input
                      type="radio"
                      name="contractType"
                      value="representation"
                      checked={formData.contractType === "representation"}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div
                      className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                        formData.contractType === "representation"
                          ? "border-[#374DF5] bg-white"
                          : "border-[#999999] bg-white"
                      }`}
                    >
                      {formData.contractType === "representation" && (
                        <div className="w-5 h-5 rounded-full bg-[#374DF5]" />
                      )}
                    </div>
                  </div>
                  <span className="text-base text-[#0A0F2D]">
                    Representation Contract
                  </span>
                </label>

                {/* Mandate */}
                <label className="flex items-center gap-3 cursor-pointer">
                  <div className="relative">
                    <input
                      type="radio"
                      name="contractType"
                      value="mandate"
                      checked={formData.contractType === "mandate"}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div
                      className={`w-8 h-8 rounded-full border-2 flex items-center justify-center ${
                        formData.contractType === "mandate"
                          ? "border-[#374DF5] bg-white"
                          : "border-[#999999] bg-white"
                      }`}
                    >
                      {formData.contractType === "mandate" && (
                        <div className="w-5 h-5 rounded-full bg-[#374DF5]" />
                      )}
                    </div>
                  </div>
                  <span className="text-base text-[#0A0F2D]">
                    Temporary Representation
                  </span>
                </label>
              </div>

              {errors.contractType && (
                <p className="text-sm text-red-500">{errors.contractType}</p>
              )}
            </div>

            {/* Country Selection - Only show for mandate type */}
            {formData.contractType === "mandate" && (
              <div className="space-y-3">
                <label className="block text-base font-medium text-black">
                  Select authorized countries for this staff member to be
                  represented in
                </label>

                <div className="relative" ref={countryDropdownRef}>
                  <button
                    type="button"
                    onClick={handleCountryDropdownToggle}
                    className="w-full h-12 px-4 border border-[#E9E9E9] rounded-[5px] flex items-center justify-between text-left"
                  >
                    <span
                      className={`text-base ${
                        formData.selectedCountries.length > 0
                          ? "text-[#1A212F]"
                          : "text-[#626262]"
                      }`}
                    >
                      {formData.selectedCountries.length > 0
                        ? `${formData.selectedCountries.length} countries selected`
                        : "Select countries"}
                    </span>
                    <svg
                      className="w-5 h-5 text-[#626262]"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth={2}
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </button>

                  {/* Country Dropdown */}
                  {showCountryDropdown && (
                    <div className="absolute z-10 w-full mt-1 bg-white border border-[#E9E9E9] rounded-[5px] shadow-lg max-h-64 overflow-hidden">
                      {/* Search Input */}
                      <div className="p-3 border-b border-gray-100">
                        <div className="relative">
                          <input
                            type="text"
                            placeholder="Search countries..."
                            value={countrySearchQuery}
                            onChange={(e) =>
                              setCountrySearchQuery(e.target.value)
                            }
                            className="w-full h-8 pl-8 pr-3 border border-[#E9E9E9] rounded text-sm placeholder-[#626262] focus:outline-none focus:ring-1 focus:ring-blue-500"
                          />
                          <svg
                            className="w-4 h-4 text-[#626262] absolute left-2 top-1/2 -translate-y-1/2"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
                            />
                          </svg>
                        </div>
                      </div>

                      {/* Countries List */}
                      <div className="max-h-48 overflow-y-auto">
                        {isLoadingCountries ? (
                          <div className="p-4 text-center text-secondary">
                            Loading countries...
                          </div>
                        ) : filteredCountries.length > 0 ? (
                          filteredCountries.map((country) => (
                            <button
                              key={country.id}
                              type="button"
                              onClick={() => handleCountrySelect(country)}
                              className={`w-full px-4 py-2 text-left hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0 ${
                                formData.selectedCountries.some(
                                  (c) => c.id === country.id
                                )
                                  ? "bg-blue-50"
                                  : ""
                              }`}
                            >
                              <div
                                className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                                  formData.selectedCountries.some(
                                    (c) => c.id === country.id
                                  )
                                    ? "border-blue-500 bg-blue-500"
                                    : "border-gray-300"
                                }`}
                              >
                                {formData.selectedCountries.some(
                                  (c) => c.id === country.id
                                ) && (
                                  <svg
                                    className="w-3 h-3 text-white"
                                    fill="currentColor"
                                    viewBox="0 0 20 20"
                                  >
                                    <path
                                      fillRule="evenodd"
                                      d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                                      clipRule="evenodd"
                                    />
                                  </svg>
                                )}
                              </div>

                              {/* Country Flag */}
                              {country.image_url && (
                                <img
                                  src={country.image_url}
                                  alt={country.name}
                                  className="w-5 h-4 object-cover rounded-sm flex-shrink-0"
                                  onError={(e) => {
                                    e.target.style.display = "none";
                                  }}
                                />
                              )}

                              <span className="text-sm flex-1">
                                {country.name}
                              </span>
                            </button>
                          ))
                        ) : (
                          <div className="p-4 text-center text-gray-500">
                            No countries found for "{countrySearchQuery}"
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>

                {/* Selected Countries Chips */}
                {formData.selectedCountries.length > 0 && (
                  <div className="flex flex-wrap gap-2 mt-2">
                    {formData.selectedCountries.slice(0, 5).map((country) => (
                      <div
                        key={country.id}
                        className="flex items-center gap-2 bg-[#E9E9E9] rounded px-2 py-1"
                      >
                        {(country.image_url || country.image_path) && (
                          <img
                            src={country.image_url || country.image_path}
                            alt={country.name}
                            className="w-4 h-3 object-cover rounded-sm"
                            onError={(e) => {
                              e.target.style.display = "none";
                            }}
                          />
                        )}
                        <span className="text-sm text-[#0A0F2D]">
                          {country.name}
                        </span>
                        <button
                          type="button"
                          onClick={() => handleRemoveCountry(country.id)}
                          className="text-[#1E1E1E] hover:text-red-500"
                        >
                          <svg
                            className="w-4 h-4"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth={2}
                              d="M6 18L18 6M6 6l12 12"
                            />
                          </svg>
                        </button>
                      </div>
                    ))}
                    {formData.selectedCountries.length > 5 && (
                      <div className="bg-[#E9E9E9] rounded px-2 py-1">
                        <span className="text-sm text-[#0A0F2D]">
                          +{formData.selectedCountries.length - 5} more
                        </span>
                      </div>
                    )}
                  </div>
                )}

                {errors.selectedCountries && (
                  <p className="text-sm text-red-500">
                    {errors.selectedCountries}
                  </p>
                )}
              </div>
            )}

            {/* Duration Selection */}
            <div className="space-y-3">
              <label className="block text-base font-medium text-black">
                Choose Representation Duration
              </label>

              <div className="flex flex-col gap-3 md:flex-row md:items-center md:gap-3">
                <div className="w-full md:flex-1">
                  <CustomDateTimePicker
                    value={
                      formData.startDate ? new Date(formData.startDate) : null
                    }
                    onChange={(date) => {
                      const formattedDate = date
                        ? date.toISOString().split("T")[0]
                        : "";
                      setFormData((prev) => ({
                        ...prev,
                        startDate: formattedDate,
                      }));
                        // Clear start date error on change
                        setErrors((prev) => ({ ...prev, startDate: "" }));
                    }}
                    placeholder="Start date"
                    error={errors.startDate}
                    minDate={null}
                    className="w-full h-12 px-4 border border-[#E9E9E9] rounded-[5px] text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <span className="text-base text-[#626262] font-medium hidden md:inline">
                  --
                </span>

                <div className="w-full md:flex-1">
                  <CustomDateTimePicker
                    value={formData.endDate ? new Date(formData.endDate) : null}
                    onChange={(date) => {
                      const formattedDate = date
                        ? date.toISOString().split("T")[0]
                        : "";
                      setFormData((prev) => ({
                        ...prev,
                        endDate: formattedDate,
                      }));
                        // Clear end date error on change
                        setErrors((prev) => ({ ...prev, endDate: "" }));
                    }}
                    placeholder="End date"
                    error={errors.endDate}
                    minDate={formData.startDate ? new Date(formData.startDate) : null}
                    className="w-full h-12 px-4 border border-[#E9E9E9] rounded-[5px] text-base focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>
              </div>
            </div>

            {/* Exclusivity checkbox - Only show for mandate type */}
            {formData.contractType === "mandate" && (
              <div className="space-y-3">
                <label className="flex items-start gap-3 cursor-pointer">
                  <div className="relative mt-1">
                    <input
                      type="checkbox"
                      name="isExclusive"
                      checked={formData.isExclusive}
                      onChange={handleInputChange}
                      className="sr-only"
                    />
                    <div
                      className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                        formData.isExclusive
                          ? "border-[#2283FF] bg-[#2283FF]"
                          : "border-[#E9E9E9] bg-white"
                      }`}
                    >
                      {formData.isExclusive && (
                        <svg
                          className="w-3 h-3 text-white"
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path
                            fillRule="evenodd"
                            d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                            clipRule="evenodd"
                          />
                        </svg>
                      )}
                    </div>
                  </div>
                  <span className="text-base text-[#0A0F2D]">
                    Make this representation exclusive
                  </span>
                </label>
              </div>
            )}

            {/* Agreement Checkbox */}
            <div className="space-y-3">
              <label className="flex items-start gap-5 cursor-pointer">
                <div className="relative mt-1">
                  <input
                    type="checkbox"
                    name="agreed"
                    checked={formData.agreed}
                    onChange={handleInputChange}
                    className="sr-only"
                  />
                  <div
                    className={`w-4 h-4 border-2 rounded flex items-center justify-center ${
                      formData.agreed
                        ? "border-[#2283FF] bg-[#2283FF]"
                        : "border-[#E9E9E9] bg-white"
                    }`}
                  >
                    {formData.agreed && (
                      <svg
                        className="w-3 h-3 text-white"
                        fill="currentColor"
                        viewBox="0 0 20 20"
                      >
                        <path
                          fillRule="evenodd"
                          d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z"
                          clipRule="evenodd"
                        />
                      </svg>
                    )}
                  </div>
                </div>
                <span className="text-base text-[#0A0F2D] leading-relaxed">
                  {getAgreementText()}
                </span>
              </label>

              {errors.agreed && (
                <p className="text-sm text-red-500">{errors.agreed}</p>
              )}
            </div>

            {/* Submit Error */}
            {errors.submit && (
              <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                <p className="text-sm text-red-600">{errors.submit}</p>
              </div>
            )}

            {/* Buttons */}
            <div className="flex flex-col gap-3 pt-4 md:flex-row md:gap-5">
              <button
                type="submit"
                disabled={isSubmitting || (isExternalSubject && coachHasImage === false && !uploadedImageUrl)}
                className="h-12 w-full md:w-auto px-6 bg-[#2283FF] text-white rounded-[5px] hover:bg-blue-600 text-base font-normal shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
              >
                {isSubmitting && (
                  <div className="animate-spin w-4 h-4 border-2 border-white border-t-transparent rounded-full" />
                )}
                {isSubmitting ? "Sending..." : "Send invite"}
              </button>
              <button
                type="button"
                onClick={() => navigate(-1)}
                disabled={isSubmitting}
                className="h-12 w-full md:w-auto px-6 border border-[#E6E6E6] text-[#1E1E1E] rounded-[5px] hover:bg-gray-50 text-base font-normal disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default InviteStaff;
